import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { api } from '@/services/api';
import { PricingCarousel } from '@/components/embed/PricingCarousel';
import { TokenCalculator } from '@/components/embed/TokenCalculator';

type View = 'Plans' | 'Calculator';

const REGISTER_BASE_URL = '/register';

export function EmbedPricingPage() {
  const [view, setView] = useState<View>('Plans');

  const { data: packages, isLoading, error } = useQuery({
    queryKey: ['packages'],
    queryFn: () => api.getPackages(),
    staleTime: 5 * 60 * 1000,
  });

  const handleSelect = (slug: string) => {
    const url = `${REGISTER_BASE_URL}?package=${encodeURIComponent(slug)}`;
    if (window.top !== window.self) {
      window.top!.location.href = url;
    } else {
      window.location.href = url;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen w-full bg-slate-900 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !packages?.length) {
    return (
      <div className="min-h-screen w-full bg-slate-900 flex items-center justify-center">
        <p className="text-slate-400 text-sm">
          Unable to load pricing. Please try again later.
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-slate-900 flex flex-col items-center font-sans">
      <style>{`.embed-hide-scrollbar::-webkit-scrollbar { display: none; }`}</style>

      <div className="w-full relative flex-1 flex items-start justify-center">
        <AnimatePresence mode="wait">
          {view === 'Plans' ? (
            <motion.div
              key="plans"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="w-full"
            >
              <PricingCarousel
                packages={packages}
                onSelect={handleSelect}
                onSwitchView={() => setView('Calculator')}
              />
            </motion.div>
          ) : (
            <motion.div
              key="calculator"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="w-full"
            >
              <TokenCalculator
                onSelect={handleSelect}
                onSwitchView={() => setView('Plans')}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="py-6 text-center">
        <a
          href="https://app.vortexnav.com"
          target="_top"
          className="text-sm text-slate-600 hover:text-slate-400 transition-colors"
        >
          Powered by VortexNav
        </a>
      </div>
    </div>
  );
}

export default EmbedPricingPage;
